import { useEffect, useMemo, useRef, useState } from 'react';
import { updateWhitelistedDomains } from '../../../../api';
import { useAtom } from 'jotai';
import { useOnboardHSAConfigAtom } from '../../../../atoms';
import { selectedCustomerAtom } from 'atoms/useSelectedCustomerAtom';

// Components
import WhitelistedDomain from './WhitelistedDomain';
import Textbox from '../../../../components/Textbox';
import OnboardContainer from '../../OnboardContainer';

// Utils
import { isValidGSCProperty } from 'utils';
import { isDirtyAtom } from 'atoms/isDirtyAtom';
import useFieldChangeTracker from 'hooks/useFieldChangeTracker';

function HSAWhitelistedDomainsForm() {
  const [hsaConfig, setHSAConfig] = useOnboardHSAConfigAtom();
  const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [domains, setDomains] = useState<string[]>([]);
  const [inputText, setInputText] = useState('');
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const [isValid, setIsValid] = useState(false);

  const [changeCount, handleFieldChange] = useFieldChangeTracker(); // Custom hook increments number to detect input change to trigger mutation - auto save

  const WhitelistedDomainSavedDraft = useMemo(() => {
    return selectedCustomer?.whitelisted_domains ?? [];
  }, [selectedCustomer]);

  // Prefill form, Update with previous HSA data if available (For the Recalculate & Draft work-flow)
  useEffect(() => {
    setDomains(WhitelistedDomainSavedDraft);
  }, [WhitelistedDomainSavedDraft]);

  const onChangeInput = (val: string) => {
    setInputText(val);
  };

  // Compare all current form fields with last saved data and set isDirty flag to true or false using "changed"
  const changed = useMemo(() => {
    const whiteListedDomainsChanged =
      WhitelistedDomainSavedDraft.length !== domains.length ||
      !WhitelistedDomainSavedDraft.every(
        (value, index) => value === domains[index]
      );

    return whiteListedDomainsChanged;
  }, [WhitelistedDomainSavedDraft, domains]);

  // Set isDirty flag for Whitelisted Domains page if any form value changed since last saved
  useEffect(() => {
    setIsDirty((prev) => ({ ...prev, 'Whitelisted Domains': changed }));
  }, [changed, setIsDirty]);

  const listRef = useRef<HTMLUListElement | null>(null);

  const onAddDomain = () => {
    if (!inputText) return;

    // Allow for multiple values to be added at once
    const values = inputText
      .trim()
      .split(/[, ]/g)
      .filter((val) => !!val.trim());

    // Check value(s) for error(s)
    for (const val of values) {
      if (!isValidGSCProperty(val)) {
        setErrorMsg('Invalid domain: ' + val);
        return;
      }
      if (domains.includes(val)) {
        setErrorMsg('Duplicate value: ' + val);
        return;
      }
    }

    // Store value
    setDomains((prev) => [...prev, ...values]);

    // Set is valid for auto save validation - validation done above
    setIsValid(true);

    // Reset input and error message
    setInputText('');
    if (errorMsg) {
      setErrorMsg(null);
    }
    handleFieldChange(); // Triggers auto save
  };

  const onEditDomain = (val: string, index: number) => {
    // Check for errors
    if (!isValidGSCProperty(val)) {
      throw new Error('Invalid domain: ' + val);
    }
    const existingIndex = domains.findIndex((value) => value === val);
    if (existingIndex >= 0 && existingIndex !== index) {
      throw new Error('Duplicate value: ' + val);
    }

    // Update value and clear error
    setDomains((prev) => prev.map((domain, i) => (i === index ? val : domain)));

    setIsValid(true);
    handleFieldChange();
  };

  const onRemoveDomain = (removeIndex: number) => {
    setDomains((prev) =>
      prev.filter((_, index) => {
        return index !== removeIndex;
      })
    );
    setIsValid(true);
    handleFieldChange();
  };

  const onSubmit = async () => {
    if (inputText) {
      throw new Error('Please add or remove current "Domain" input value.');
    }

    // Check if any inputs are in editing state
    const inputs = Array.from(listRef.current?.children || [])
      .map((li) => li.querySelector('input'))
      .filter((input) => !!input);

    for (let i = 0; i < inputs.length; i++) {
      if (!inputs[i]?.disabled) {
        inputs[i]?.focus();
        throw new Error('Unsaved changes');
      }
    }

    if (!hsaConfig?.customer_id) {
      throw new Error('Customer ID is required');
    }
    if (!hsaConfig.hsa_id) {
      throw new Error('HSA ID is required.');
    }
    if (!domains.length) {
      return {
        message: 'submitting whilted domains form...',
      };
    }

    const res = await updateWhitelistedDomains(
      hsaConfig.customer_id,
      hsaConfig.hsa_id,
      domains
    );
    setHSAConfig(res.data);
    setSelectedCustomer((prev) => ({
      ...prev,
      whitelisted_domains: domains ?? [],
    }));

    return res;
  };

  // JSX
  return (
    <OnboardContainer
      header="Whitelisted Domains"
      onSubmit={onSubmit}
      changeCount={changeCount}
      isValidInput={isValid}
    >
      <div className="space-y-8">
        <div className="flex">
          <Textbox
            label="Domain:"
            tooltip="onboarding.whitelisted_domains"
            className="flex-1"
            placeholder="Enter or paste domains (e.g. http://www.domain.com or sc-domain:domain.com)"
            inputClassName="rounded-tr-none rounded-br-none h-full"
            onChange={onChangeInput}
            onBlur={() => {
              if (!inputText && errorMsg) {
                setErrorMsg(null);
              }
            }}
            value={inputText}
            onKeyUp={(evt) => {
              if (evt.key === 'Enter') {
                onAddDomain();
              }
            }}
            errorMessage={errorMsg}
          />
          <button
            type="button"
            className="bg-blue-300 rounded-tr-md rounded-br-md py-2 px-5 h-full mt-7"
            onClick={onAddDomain}
          >
            +Add
          </button>
        </div>

        <hr className="border-t-2 border-t-gray-300" />

        <div className="flex flex-col gap-4 -translate-y-2">
          <div className="flex justify-between">
            <span className="font-bold">
              Current Whitelisted Domains ({domains.length})
            </span>
            <button
              type="button"
              className="text-error underline"
              onClick={() => setDomains([])}
            >
              Clear All
            </button>
          </div>
          <ul ref={listRef} className="flex flex-col gap-4 -translate-y-2">
            {domains.map((domain, index) => (
              <li key={index}>
                <WhitelistedDomain
                  value={domain}
                  index={index}
                  onChange={onEditDomain}
                  onRemove={onRemoveDomain}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </OnboardContainer>
  );
}

export default HSAWhitelistedDomainsForm;
